import uuid from './uuid';

/**
 * The op queue (docs/local-first-food-data.md, phase 3): small records of writes that must work
 * offline, persisted in the local-first database's `ops` store, sent in order per (namespace,
 * budget), and applied by the server at most once per `client_id`. Generic: it knows nothing of what
 * an op does, only how to keep it and send it. Food is its first user, transactions its second.
 *
 * - Write-ahead: enqueue() puts the op and bumps `meta.seq` in one transaction before it resolves.
 *   If that write fails the op is still sent, from memory, and the queue says it is not persistent.
 * - Order: within one (namespace, budget) by `seq`. Budgets are independent, so one budget's
 *   failures never hold another's ops back. A batch is up to MAX_BATCH_OPS ops and MAX_BATCH_BYTES.
 * - Only the signed-in user's ops are sent (`currentUserId`).
 * - One sender at a time across tabs and the service worker: the Web Lock SENDER_LOCK is held for
 *   one request, and never across a backoff wait. The ops are read again inside it, so ops another
 *   sender has finished are not sent again.
 * - Each answered op's result goes to `op_results` in the same transaction that deletes the op,
 *   then out on the channel, so any tab, or a later load, can fold it.
 *
 * Failures (the design's "Failure semantics"): a network error, 5xx or 404 backs the budget off,
 * 2 s doubling to 5 min with jitter, and counts against no op. So does a request that never answers:
 * `send` gives up on one after a timeout (sendOps.js), since the sender lock is held while it waits.
 * A `retry` result counts against the op it names unless the server marked it `transient`; ten
 * counted retries reject it. `skipped` counts nothing. A whole-request 400 or 413 sends that batch's ops one at a time, and one still
 * refused alone is rejected. 401 pauses every op until resume(), or until something says the
 * session may work again: coming online, the page becoming visible, or another sender's answer for
 * this user. The next request finds out, and a 401 pauses again. 403 rejects the budget's ops.
 */

export const OPS = 'ops';
export const RESULTS = 'op_results';
export const META = 'meta';
export const SEQ = 'seq';

export const SENDER_LOCK = 'local-first-sender';
export const CHANNEL = 'local-first';
export const MAX_BATCH_OPS = 25;
export const MAX_BATCH_BYTES = 64 * 1024;
export const MAX_COUNTED_RETRIES = 10;
export const BACKOFF_FIRST_MS = 2000;
export const BACKOFF_MAX_MS = 5 * 60 * 1000;
/** How long an applied op's result is kept when nothing has folded it. Rejected ones stay until dismissed. */
export const RESULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const NEEDS_CONNECTION = 'Needs a connection';
export const KEPT_FAILING = 'The server kept failing on this change';

/** Where an op that could not be written is numbered: after any op the database holds. */
const MEMORY_SEQ = 1e12;

export const groupKey = (namespace, budgetId) => `${namespace}|${budgetId}`;

const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

export const bySeq = (a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0);

/** An op as it is sent: without the fields only this browser needs. */
export const wireOp = (op) => ({ client_id: op.client_id, type: op.type, v: op.v, payload: op.payload, created_at: op.created_at });

/** A new op, before it has its `seq`. */
export const createOp = ({ namespace, userId, budgetId, type, payload, v = 1, clientId = uuid(), now = Date.now() }) => ({
	client_id: clientId,
	type,
	v,
	payload,
	created_at: Math.floor(now / 1000),
	namespace,
	user_id: userId,
	budget_id: budgetId,
	seq: null,
	attempts: 0,
	last_error: null,
});

/** An in-page stand-in for Web Locks, where they are missing: one holder per name at a time. */
export const localLocks = () => {
	const tails = new Map();
	return {
		request(name, callback) {
			const run = (tails.get(name) || Promise.resolve()).then(() => callback());
			tails.set(name, run.catch(() => {}));
			return run;
		},
	};
};

const defaultLocks = () => (typeof navigator !== 'undefined' && navigator.locks ? navigator.locks : localLocks());

const defaultChannel = (name) => (typeof BroadcastChannel === 'function' ? new BroadcastChannel(name) : null);

const byteLength = (text) => (typeof TextEncoder === 'function' ? new TextEncoder().encode(text).length : text.length);

/** The error an online-only request gets while the queue cannot drain. */
export const needsConnection = (cause = null) => {
	const error = new Error(NEEDS_CONNECTION);
	error.responseJSON = { error: NEEDS_CONNECTION };
	if(cause) {
		error.cause = cause;
	}
	return error;
};

/** A rejection made here rather than by the server: the op is never sent again. */
const localRejection = (op, error) => ({ client_id: op.client_id, status: 'rejected', error, local: true, changes: {} });

const errorOf = (response) => (response && response.body && response.body.error) ||
	(response && response.error && response.error.message) || null;

/**
 * @param {Object} options
 * @param {Object} options.db db.js or memoryDb()
 * @param {Function} options.send (namespace, budgetId, ops, { userId }) => Promise<{ status, body }>, rejecting on
 *   a network error or a timeout; `userId` is whose ops they are, which the server checks against who is signed in
 * @param {Function} options.currentUserId () => the signed-in user's id, or null
 * @param {Function} options.onResult (record, { remote }) for every result, this sender's or another's
 * @param {Function} options.onState (state) whenever sending, backoff, pause, persistence or `unsent`, the
 *   signed-in user's ops not yet sent in every namespace and budget, change
 * @param {Function} options.onEnqueued (op, { remote }) for an op another tab enqueued
 */
export function createOpQueue({
	db,
	send,
	currentUserId = () => null,
	onResult = () => {},
	onState = () => {},
	onEnqueued = () => {},
	now = () => Date.now(),
	schedule = (callback, ms) => setTimeout(callback, ms),
	cancel = (timer) => clearTimeout(timer),
	random = Math.random,
	locks = defaultLocks(),
	channelFactory = defaultChannel,
	isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false,
	requestSync = () => {},
	newId = uuid,
} = {}) {
	/** Ops whose write failed: sent from here, and lost on reload. */
	const memoryOps = new Map();
	/** Ops answered whose result could not be written: never sent again by this queue. */
	const finished = new Set();
	const groups = new Map();
	const waiters = [];
	let paused = null;
	let persistent = !!db.persistent;
	let stopped = false;
	let flushing = null;
	let again = false;
	let memorySeq = 0;
	/** How many ops the signed-in user has not had sent, in every namespace and budget; null until counted. */
	let unsent = null;
	let counting = null;
	let countAgain = false;

	let channel = null;
	try {
		channel = channelFactory(CHANNEL);
	} catch(error) {
		channel = null;
	}

	const post = (message) => {
		if(!channel) {
			return;
		}
		try {
			channel.postMessage(message);
		} catch(error) {
			console.error(error);
		}
	};

	const groupState = (key) => {
		if(!groups.has(key)) {
			groups.set(key, { failures: 0, retryAt: 0, timer: null, sending: false, lastError: null, alone: new Set() });
		}
		return groups.get(key);
	};

	const state = () => {
		const byGroup = {};
		groups.forEach((group, key) => {
			byGroup[key] = { sending: group.sending, lastError: group.lastError, nextRetryAt: group.retryAt || null, failures: group.failures };
		});
		return { paused, persistent, unsent, groups: byGroup };
	};

	const emit = () => {
		try {
			onState(state());
		} catch(error) {
			console.error(error);
		}
	};

	const deliver = (record, meta) => {
		try {
			onResult(record, meta);
		} catch(error) {
			console.error(error);
		}
	};

	const mine = (op) => !!op && sameId(op.user_id, currentUserId());

	const inGroup = (namespace, budgetId) => (op) => op.namespace === namespace && sameId(op.budget_id, budgetId);

	/** Every op held, the database's and memory's, oldest first. */
	const readOps = () => db.run([OPS], 'readonly', (tx) => tx.entries(OPS))
		.then((entries) => entries.map((entry) => entry.value), (error) => {
			console.error(error);
			return [];
		})
		.then((stored) => {
			const byId = new Map(stored.filter(Boolean).map((op) => [op.client_id, op]));
			memoryOps.forEach((op, id) => byId.set(id, op));
			return [...byId.values()].filter((op) => !finished.has(op.client_id)).sort(bySeq);
		});

	/**
	 * Counts the signed-in user's ops again, and tells onState when the count has moved. Called after
	 * anything that can change it here, and when another tab or the service worker says it has.
	 */
	const recount = () => {
		if(counting) {
			countAgain = true;
			return counting;
		}
		counting = (async () => {
			do {
				countAgain = false;
				const count = (await readOps()).filter(mine).length;
				if(count !== unsent) {
					unsent = count;
					emit();
				}
			} while(countAgain);
		})()
			.catch((error) => console.error(error))
			.then(() => {
				counting = null;
			});
		return counting;
	};

	const myGroups = (ops) => {
		const found = new Map();
		ops.filter(mine).forEach((op) => {
			const key = groupKey(op.namespace, op.budget_id);
			if(!found.has(key)) {
				found.set(key, { namespace: op.namespace, budgetId: op.budget_id });
			}
		});
		return [...found.values()];
	};

	/** Lifts a 401's pause, for the next request to find out whether the session works now. */
	const unpause = () => {
		if(paused) {
			paused = null;
			emit();
		}
	};

	const resetBackoff = () => {
		groups.forEach((group) => {
			group.retryAt = 0;
			if(group.timer !== null) {
				cancel(group.timer);
				group.timer = null;
			}
		});
	};

	const removeWaiter = (waiter) => {
		const index = waiters.indexOf(waiter);
		if(index !== -1) {
			waiters.splice(index, 1);
		}
	};

	const failWaiters = (match, cause = null) => {
		[...waiters].filter(match).forEach((waiter) => {
			removeWaiter(waiter);
			waiter.reject(needsConnection(cause));
		});
	};

	const settleWaiters = () => {
		if(!waiters.length) {
			return Promise.resolve();
		}
		return readOps().then((ops) => {
			[...waiters].forEach((waiter) => {
				if(!ops.filter(mine).some(inGroup(waiter.namespace, waiter.budgetId))) {
					removeWaiter(waiter);
					waiter.resolve();
				}
			});
		});
	};

	const succeeded = (group) => {
		group.failures = 0;
		group.retryAt = 0;
		group.lastError = null;
		if(group.timer !== null) {
			cancel(group.timer);
			group.timer = null;
		}
	};

	const backOff = (key, group, message) => {
		group.failures += 1;
		const base = Math.min(BACKOFF_FIRST_MS * 2 ** (group.failures - 1), BACKOFF_MAX_MS);
		const delay = Math.round(base / 2 + (random() * base) / 2);
		group.retryAt = now() + delay;
		group.lastError = message;
		if(group.timer !== null) {
			cancel(group.timer);
		}
		group.timer = schedule(() => {
			group.timer = null;
			group.retryAt = 0;
			flush();
		}, delay);
		failWaiters((waiter) => waiter.key === key, message);
	};

	const resultRecord = (op, result) => ({
		client_id: op.client_id,
		namespace: op.namespace,
		user_id: op.user_id,
		budget_id: op.budget_id,
		result,
		op: { type: op.type, v: op.v, payload: op.payload, seq: op.seq, created_at: op.created_at },
		at: now(),
	});

	/**
	 * Writes the results of `finals` ({ op, result }) and deletes their ops, and puts back `updates`,
	 * all in one transaction; then tells the channel and onResult.
	 */
	const store = (finals, updates) => {
		if(!finals.length && !updates.length) {
			return Promise.resolve();
		}
		const records = finals.map(({ op, result }) => resultRecord(op, result));
		return db.run([OPS, RESULTS], 'readwrite', (tx) => Promise.all([
			...records.map((record) => tx.put(RESULTS, record.client_id, record)),
			...finals.map(({ op }) => tx.delete(OPS, op.client_id)),
			...updates.map((op) => tx.put(OPS, op.client_id, op)),
		])).then(() => true, (error) => {
			console.error(error);
			return false;
		}).then((stored) => {
			finals.forEach(({ op }) => {
				memoryOps.delete(op.client_id);
				if(!stored) {
					finished.add(op.client_id);
				}
			});
			updates.forEach((op) => {
				if(!stored || memoryOps.has(op.client_id)) {
					memoryOps.set(op.client_id, op);
				}
			});
			records.forEach((record) => {
				post({ kind: 'result', result: record });
				deliver(record, { remote: false });
			});
			if(finals.length) {
				recount();
			}
		});
	};

	/** The first ops of a budget that go in one request. */
	const batchOf = (ops, alone) => {
		if(alone.has(ops[0].client_id)) {
			return [ops[0]];
		}
		const batch = [];
		let bytes = 2;
		for(const op of ops) {
			const size = byteLength(JSON.stringify(wireOp(op))) + 1;
			if(alone.has(op.client_id) || (batch.length && (batch.length >= MAX_BATCH_OPS || bytes + size > MAX_BATCH_BYTES))) {
				break;
			}
			batch.push(op);
			bytes += size;
		}
		return batch;
	};

	/** What a request's answer does to the budget's ops. 'more' to go on sending, 'stop' otherwise. */
	const handle = async (key, group, ops, batch, response) => {
		const status = Number(response.status) || 0;
		if(status === 401) {
			paused = 'auth';
			group.lastError = errorOf(response) || 'Sign in again to send changes.';
			failWaiters(() => true, group.lastError);
			return 'stop';
		}
		if(status === 403) {
			const error = errorOf(response) || 'You are not a member of that budget.';
			await store(ops.map((op) => ({ op, result: localRejection(op, error) })), []);
			succeeded(group);
			return 'more';
		}
		if(status === 400 || status === 413) {
			if(batch.length > 1) {
				batch.forEach((op) => group.alone.add(op.client_id));
				return 'more';
			}
			group.alone.delete(batch[0].client_id);
			await store([{ op: batch[0], result: localRejection(batch[0], errorOf(response) || 'The server refused this change.') }], []);
			return 'more';
		}
		if(status < 200 || status >= 300) {
			backOff(key, group, errorOf(response) || (status ? `The server answered ${status}.` : 'No connection.'));
			return 'stop';
		}
		const results = new Map();
		((response.body && response.body.results) || []).forEach((result) => {
			if(result && result.client_id) {
				results.set(result.client_id, result);
			}
		});
		const finals = [];
		const updates = [];
		let retrying = null;
		batch.forEach((op) => {
			const result = results.get(op.client_id);
			if(!result) {
				return;
			}
			if(result.status === 'applied' || result.status === 'rejected') {
				group.alone.delete(op.client_id);
				finals.push({ op, result });
			} else if(result.status === 'retry') {
				retrying = result.error || 'The server could not apply a change.';
				const attempts = (Number(op.attempts) || 0) + (result.transient ? 0 : 1);
				if(attempts >= MAX_COUNTED_RETRIES) {
					finals.push({ op, result: localRejection(op, KEPT_FAILING) });
				} else {
					updates.push({ ...op, attempts, last_error: result.error || null });
				}
			}
		});
		await store(finals, updates);
		if(retrying) {
			backOff(key, group, retrying);
			return 'stop';
		}
		if(!finals.length) {
			backOff(key, group, 'The server answered none of the changes sent.');
			return 'stop';
		}
		succeeded(group);
		return 'more';
	};

	/** One request for a budget, inside the sender lock. */
	const sendBatch = async (namespace, budgetId, key, group) => {
		const ops = (await readOps()).filter(mine).filter(inGroup(namespace, budgetId));
		if(!ops.length || stopped || paused) {
			return 'stop';
		}
		const batch = batchOf(ops, group.alone);
		group.sending = true;
		emit();
		let response;
		try {
			response = await send(namespace, budgetId, batch.map(wireOp), { userId: currentUserId() });
		} catch(error) {
			response = { status: 0, error };
		}
		group.sending = false;
		const outcome = await handle(key, group, ops, batch, response || { status: 0 });
		emit();
		return outcome;
	};

	const sendGroup = async ({ namespace, budgetId }) => {
		const key = groupKey(namespace, budgetId);
		const group = groupState(key);
		for(;;) {
			if(stopped || paused || !isOnline() || group.retryAt > now()) {
				return;
			}
			const outcome = await locks.request(SENDER_LOCK, () => sendBatch(namespace, budgetId, key, group));
			await settleWaiters();
			if(outcome !== 'more') {
				return;
			}
		}
	};

	const remaining = () => readOps().then((ops) => ({ remaining: ops.filter(mine).length }));

	/**
	 * Sends every budget's ops that can go now. Called while a flush is running, it runs once more
	 * after it. Resolves `{ remaining }`, the signed-in user's ops still held.
	 */
	function flush() {
		if(stopped) {
			return remaining();
		}
		if(flushing) {
			again = true;
			return flushing;
		}
		flushing = (async () => {
			do {
				again = false;
				for(const group of myGroups(await readOps())) {
					await sendGroup(group);
				}
			} while(again && !stopped);
		})()
			.catch((error) => console.error(error))
			.then(() => {
				flushing = null;
				return settleWaiters();
			})
			.then(remaining);
		return flushing;
	}

	if(channel) {
		const onMessage = (event) => {
			const data = (event && event.data) || {};
			if(data.kind === 'result' && data.result) {
				deliver(data.result, { remote: true });
				recount();
				// Another sender was answered for this user, so the session works again: send what waited on it.
				if(paused === 'auth' && !stopped && mine(data.result)) {
					unpause();
					flush();
				}
			} else if(data.kind === 'enqueued' && data.op) {
				try {
					onEnqueued(data.op, { remote: true });
				} catch(error) {
					console.error(error);
				}
				recount();
			} else if(data.kind === 'logout') {
				queue.stop();
			}
		};
		if(typeof channel.addEventListener === 'function') {
			channel.addEventListener('message', onMessage);
		} else {
			channel.onmessage = onMessage;
		}
	}

	const queue = {
		/**
		 * Persists a new op for the user's budget and sends it when it can. Resolves with the op, its
		 * `seq` set, once it is written (or kept in memory when it could not be).
		 */
		enqueue(namespace, userId, budgetId, type, payload, { v = 1, clientId = newId() } = {}) {
			const op = createOp({ namespace, userId, budgetId, type, payload, v, clientId, now: now() });
			return db.run([OPS, META], 'readwrite', (tx) => tx.get(META, SEQ).then((last) => {
				op.seq = (Number(last) || 0) + 1;
				return Promise.all([tx.put(META, SEQ, op.seq), tx.put(OPS, op.client_id, op)]);
			})).catch((error) => {
				console.error('An op could not be written; it is kept until this page closes.', error);
				memorySeq += 1;
				op.seq = MEMORY_SEQ + memorySeq;
				memoryOps.set(op.client_id, op);
				if(persistent) {
					persistent = false;
					emit();
				}
			}).then(() => {
				post({ kind: 'enqueued', op });
				recount();
				try {
					requestSync();
				} catch(error) {
					console.error(error);
				}
				if(!stopped && isOnline()) {
					flush();
				}
				return op;
			});
		},

		flush,

		/**
		 * Resolves once the budget has no op of the signed-in user's left: the barrier an online-only
		 * request waits at, so it never runs before an op it might conflict with. With none, at once.
		 * While the queue cannot drain (offline, paused for sign-in, or backing off after a failure)
		 * it rejects with NEEDS_CONNECTION.
		 */
		drained(namespace, budgetId) {
			return readOps().then((ops) => {
				if(!ops.filter(mine).some(inGroup(namespace, budgetId))) {
					return undefined;
				}
				if(stopped || paused || !isOnline()) {
					throw needsConnection();
				}
				const key = groupKey(namespace, budgetId);
				return new Promise((resolve, reject) => {
					waiters.push({ key, namespace, budgetId, resolve, reject });
					const group = groupState(key);
					group.retryAt = 0;
					if(group.timer !== null) {
						cancel(group.timer);
						group.timer = null;
					}
					flush();
				});
			});
		},

		/** The signed-in user's ops for a budget, oldest first, and the results stored for it, read together. */
		load(namespace, budgetId) {
			const belongs = (value) => mine(value) && inGroup(namespace, budgetId)(value);
			return db.run([OPS, RESULTS], 'readonly', (tx) => Promise.all([tx.entries(OPS), tx.entries(RESULTS)]))
				.then(([ops, results]) => ({
					ops: ops.map((entry) => entry.value),
					results: results.map((entry) => entry.value).filter(belongs),
				}), (error) => {
					console.error(error);
					return { ops: [], results: [] };
				})
				.then(({ ops, results }) => {
					const byId = new Map(ops.filter(Boolean).map((op) => [op.client_id, op]));
					memoryOps.forEach((op, id) => byId.set(id, op));
					return {
						ops: [...byId.values()].filter((op) => !finished.has(op.client_id) && belongs(op)).sort(bySeq),
						results: results.sort((a, b) => bySeq(a.op || {}, b.op || {})),
					};
				});
		},

		/** Deletes stored results by client_id: folded, and no longer needed. */
		removeResults(clientIds) {
			if(!clientIds.length) {
				return Promise.resolve();
			}
			return db.run([RESULTS], 'readwrite', (tx) => Promise.all(clientIds.map((id) => tx.delete(RESULTS, id))))
				.catch((error) => console.error(error));
		},

		/** Adds `fields` to the stored results of `clientIds`, such as a note that the user has been told of them. */
		markResults(clientIds, fields) {
			if(!clientIds.length) {
				return Promise.resolve();
			}
			return db.run([RESULTS], 'readwrite', (tx) => Promise.all(clientIds.map((id) => tx.get(RESULTS, id)
				.then((record) => (record ? tx.put(RESULTS, id, { ...record, ...fields }) : undefined)))))
				.catch((error) => console.error(error));
		},

		/** Deletes results older than `maxAge` ms that nothing folded, except rejected ones, which wait to be dismissed. */
		pruneResults(maxAge = RESULT_TTL_MS) {
			const before = now() - maxAge;
			return db.run([RESULTS], 'readwrite', (tx) => tx.entries(RESULTS).then((entries) => Promise.all(entries
				.filter(({ value }) => !value || (Number(value.at) < before && !(value.result && value.result.status === 'rejected')))
				.map(({ key }) => tx.delete(RESULTS, key)))))
				.catch((error) => console.error(error));
		},

		/** How many ops `userId` (the signed-in user when not given) has not had sent. */
		count(userId = currentUserId()) {
			return readOps().then((ops) => ops.filter((op) => sameId(op.user_id, userId)).length);
		},

		/** Sends again after a 401, once the user has signed in, and counts that user's ops. */
		resume() {
			unpause();
			resetBackoff();
			recount();
			return flush();
		},

		/** Counts the signed-in user's unsent ops again (state's `unsent`), as after another sender's work. */
		recount,

		/**
		 * Sends on the browser's `online` event and when the page becomes visible, ahead of any backoff
		 * timer and past a 401's pause, since the user may have signed in again in another tab; a 401
		 * pauses it again. Gives up on online-only requests waiting while it goes offline. Both also
		 * count again, for a browser without a channel, where ops another tab sent are only seen this
		 * way. Returns a function that stops watching.
		 */
		watch(target = window, doc = document) {
			const wake = () => {
				if(stopped) {
					return;
				}
				unpause();
				resetBackoff();
				recount();
				flush();
			};
			const offline = () => failWaiters(() => true);
			const visible = () => {
				if(doc.visibilityState === 'visible') {
					wake();
				}
			};
			target.addEventListener('online', wake);
			target.addEventListener('offline', offline);
			doc.addEventListener('visibilitychange', visible);
			return () => {
				target.removeEventListener('online', wake);
				target.removeEventListener('offline', offline);
				doc.removeEventListener('visibilitychange', visible);
			};
		},

		state,

		get persistent() {
			return persistent;
		},

		/** Stops sending for good: timers cancelled, the channel closed, waiting requests failed. */
		stop() {
			if(stopped) {
				return;
			}
			stopped = true;
			groups.forEach((group) => {
				if(group.timer !== null) {
					cancel(group.timer);
					group.timer = null;
				}
			});
			failWaiters(() => true);
			if(channel && typeof channel.close === 'function') {
				try {
					channel.close();
				} catch(error) {
					console.error(error);
				}
			}
		},
	};
	return queue;
}
